import { useId, type ReactNode } from 'react';
import * as RadixCheckbox from '@radix-ui/react-checkbox';
import { cn } from '@core/utils/cn';
import { Icon } from './Icon';

/**
 * Replaces `mat-checkbox`.
 *
 * Radix's native `checked="indeterminate"` is exact parity for Material's
 * `[indeterminate]`, which matters for two screens that would otherwise need
 * hand-rolled tri-state logic:
 *   - `assign-activities` — the 3-level group → sub-group → action permission tree
 *   - `extracted-data-dialog` — "Select All Available Fields"
 */
export type CheckedState = boolean | 'indeterminate';

interface CheckboxProps {
  checked: CheckedState;
  onCheckedChange: (checked: CheckedState) => void;
  label?: ReactNode;
  disabled?: boolean;
  name?: string;
  className?: string;
  labelClassName?: string;
  /** Accessible name when `label` is omitted (e.g. a checkbox inside a table row). */
  ariaLabel?: string;
}

export function Checkbox({
  checked,
  onCheckedChange,
  label,
  disabled,
  name,
  className,
  labelClassName,
  ariaLabel,
}: CheckboxProps) {
  const id = useId();

  const box = (
    <RadixCheckbox.Root
      id={id}
      name={name}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      aria-label={label ? undefined : ariaLabel}
      className={cn(
        'flex h-4 w-4 flex-none items-center justify-center rounded-[2px] border transition-colors',
        'border-gray-400 bg-surface',
        'data-[state=checked]:border-accent data-[state=checked]:bg-accent',
        'data-[state=indeterminate]:border-accent data-[state=indeterminate]:bg-accent',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
        'disabled:cursor-not-allowed disabled:opacity-45',
        className,
      )}
    >
      <RadixCheckbox.Indicator className="flex items-center justify-center text-white">
        {checked === 'indeterminate' ? (
          <span className="block h-[2px] w-2.5 rounded-full bg-current" />
        ) : (
          <Icon size="xs" className="text-[14px] leading-none">
            check
          </Icon>
        )}
      </RadixCheckbox.Indicator>
    </RadixCheckbox.Root>
  );

  if (!label) return box;

  return (
    <div className="flex items-center gap-2">
      {box}
      <label
        htmlFor={id}
        className={cn(
          'cursor-pointer text-base text-fg-2 select-none',
          disabled && 'cursor-not-allowed opacity-45',
          labelClassName,
        )}
      >
        {label}
      </label>
    </div>
  );
}
